import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const initialForm = {
  code_name: '',
  discount_type: 'percentage',
  discount_value: '',
  min_order_value: '',
  expiry_date: '',
  usage_limit: '',
  service_type: 'both',
};

const PromoCodeForm = ({ onSuccess }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const validate = () => {
    const errs = {};
    if (!form?.code_name?.trim()) errs.code_name = 'Code name is required';
    else if (!/^[A-Z0-9_-]+$/?.test(form?.code_name?.toUpperCase())) errs.code_name = 'Only letters, numbers, hyphens, underscores';
    if (!form?.discount_value || isNaN(form?.discount_value) || parseFloat(form?.discount_value) <= 0) errs.discount_value = 'Enter a valid discount value';
    if (form?.discount_type === 'percentage' && parseFloat(form?.discount_value) > 100) errs.discount_value = 'Percentage cannot exceed 100';
    if (!form?.expiry_date) errs.expiry_date = 'Expiry date is required';
    else if (new Date(form?.expiry_date) <= new Date()) errs.expiry_date = 'Expiry date must be in the future';
    if (!form?.usage_limit || isNaN(form?.usage_limit) || parseInt(form?.usage_limit) < 1) errs.usage_limit = 'Enter a valid usage limit';
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm(prev => ({ ...prev, [name]: name === 'code_name' ? value?.toUpperCase() : value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      const { error } = await supabase?.from('promo_codes')?.insert({
        code_name: form?.code_name?.trim(),
        discount_type: form?.discount_type,
        discount_value: parseFloat(form?.discount_value),
        min_order_value: parseFloat(form?.min_order_value) || 0,
        expiry_date: new Date(form?.expiry_date)?.toISOString(),
        usage_limit: parseInt(form?.usage_limit),
        service_type: form?.service_type,
        promo_status: 'active',
      });
      if (error) throw error;
      setSuccessMsg(`Promo code "${form?.code_name}" created successfully!`);
      setForm(initialForm);
      setErrors({});
      onSuccess?.();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setErrors({ submit: err?.message || 'Failed to create promo code' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.2)' }}>
          <Icon name="Tag" size={18} color="#D4AF37" />
        </div>
        <h2 className="text-lg font-semibold text-white">Create Promo Code</h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        {successMsg && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
            <Icon name="CheckCircle" size={16} />
            <span>{successMsg}</span>
          </div>
        )}
        {errors?.submit && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <Icon name="AlertCircle" size={16} />
            <span>{errors?.submit}</span>
          </div>
        )}

        {/* Code Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Code Name <span className="text-red-500">*</span></label>
          <input
            type="text"
            name="code_name"
            value={form?.code_name}
            onChange={handleChange}
            placeholder="e.g. SUMMER20"
            className={`w-full px-4 py-2.5 rounded-xl border text-sm font-mono tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors?.code_name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
          />
          {errors?.code_name && <p className="mt-1 text-xs text-red-500">{errors?.code_name}</p>}
        </div>

        {/* Discount Type + Value */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Discount Type</label>
            <select
              name="discount_type"
              value={form?.discount_type}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
            >
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed Amount (KES)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Discount Value <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">
                {form?.discount_type === 'percentage' ? '%' : 'KES'}
              </span>
              <input
                type="number"
                name="discount_value"
                value={form?.discount_value}
                onChange={handleChange}
                placeholder="0"
                min="0"
                max={form?.discount_type === 'percentage' ? 100 : undefined}
                className={`w-full pl-12 pr-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors?.discount_value ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
              />
            </div>
            {errors?.discount_value && <p className="mt-1 text-xs text-red-500">{errors?.discount_value}</p>}
          </div>
        </div>

        {/* Min Order + Expiry */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Min Order Value (KES)</label>
            <input
              type="number"
              name="min_order_value"
              value={form?.min_order_value}
              onChange={handleChange}
              placeholder="0 (no minimum)"
              min="0"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Expiry Date <span className="text-red-500">*</span></label>
            <input
              type="datetime-local"
              name="expiry_date"
              value={form?.expiry_date}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors?.expiry_date ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
            />
            {errors?.expiry_date && <p className="mt-1 text-xs text-red-500">{errors?.expiry_date}</p>}
          </div>
        </div>

        {/* Usage Limit + Service Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Usage Limit <span className="text-red-500">*</span></label>
            <input
              type="number"
              name="usage_limit"
              value={form?.usage_limit}
              onChange={handleChange}
              placeholder="e.g. 500"
              min="1"
              className={`w-full px-4 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors?.usage_limit ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
            />
            {errors?.usage_limit && <p className="mt-1 text-xs text-red-500">{errors?.usage_limit}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Applicable Service</label>
            <select
              name="service_type"
              value={form?.service_type}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
            >
              <option value="both">Ride & Delivery</option>
              <option value="ride">Ride Only</option>
              <option value="delivery">Delivery Only</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: loading ? '#6b7280' : 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)' }}
        >
          {loading ? (
            <><Icon name="Loader2" size={16} className="animate-spin" /> Creating...</>
          ) : (
            <><Icon name="Plus" size={16} /> Create Promo Code</>
          )}
        </button>
      </form>
    </div>
  );
};

export default PromoCodeForm;
